import React from 'react';

import { PREFERRED_LANGUAGE_OPTIONS } from '@/services/types/matter-client-types';

import { ClientPhotoIdField } from './client-photo-id/ClientPhotoIdField';
import { ClientFormField } from './ClientFormField';
import { HipaaSignatureField } from './HipaaSignatureField';
import { InjuriesFields } from './InjuriesFields';
import { InteractiveDisplayField } from './InteractiveDisplayField';
import { PhoneNumberFields } from './PhoneNumberFields';

/** Calculate age in years from a date string (YYYY-MM-DD). Returns null if invalid. */

const calculateAge = (dateString: string): number | null => {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}/.test(dateString)) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const today = new Date();
  const birthDate = new Date(year, month - 1, day);
  if (birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) {
    return null;
  }
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
};


interface ClientFormFieldDefinition {
  name: string;
  label: string;
  placeholder?: string;
  type?: string;
  multiline?: boolean;
  pattern?: RegExp;
  maxLength?: number;
  isSelect?: boolean;
  options?: { value: string; label: string }[];
}

interface ClientFormProps {
  isEditing: boolean;
  clientData: Record<string, any>;
  onFieldChange: (name: string, value: string) => void;
  clientId?: string;
  matterId?: string;
  /** Field names to highlight as needing attention (e.g., ['first_name', 'address.line1']) */
  highlightFields?: string[];
}

// Define the form fields structure
const CLIENT_FIELDS: ClientFormFieldDefinition[] = [
  { name: 'name', label: 'Name', placeholder: 'Enter client name' },
  {
    name: 'first_name',
    label: 'First Name',
    placeholder: 'Enter first name',
  },
  {
    name: 'last_name',
    label: 'Last Name',
    placeholder: 'Enter last name',
  },
  {
    name: 'email',
    label: 'Email',
    placeholder: 'Enter email address',
    type: 'email',
  },
  {
    name: 'date_of_birth',
    label: 'Date of Birth',
    placeholder: 'Select date of birth',
    type: 'date',
  },
  {
    name: 'gender',
    label: 'Gender',
    placeholder: 'Select gender',
    isSelect: true,
    options: [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    name: 'ssn',
    label: 'Social Security Number',
    placeholder: 'XXX-XX-XXXX',
    maxLength: 11,
  },
  {
    name: 'last_four_of_ssn',
    label: 'Last 4 of SSN',
    placeholder: 'Enter last 4 digits',
    pattern: /[^0-9]/g,
    maxLength: 4,
  },
  {
    name: 'preferred_language',
    label: 'Preferred Language',
    placeholder: 'Select preferred language',
    isSelect: true,
    options: [...PREFERRED_LANGUAGE_OPTIONS],
  },
  {
    name: 'address.line1',
    label: 'Address Line 1',
    placeholder: 'Enter street address',
  },
  {
    name: 'address.line2',
    label: 'Address Line 2',
    placeholder: 'Enter address line 2',
  },
  {
    name: 'address.line3',
    label: 'Address Line 3',
    placeholder: 'Enter address line 3',
  },
  { name: 'address.city', label: 'City', placeholder: 'Enter city' },
  { name: 'address.state', label: 'State', placeholder: 'Enter state' },
  {
    name: 'address.postcode',
    label: 'Zip Code',
    placeholder: 'Enter zip code',
  },
];

// Shared layout component for both display and edit modes
interface ClientFieldsLayoutProps {
  fields: ClientFormFieldDefinition[];
  renderField: (
    field: ClientFormFieldDefinition,
    index: number,
  ) => React.ReactNode;
  highlightFields?: string[];
}

const ClientFieldsLayout: React.FC<ClientFieldsLayoutProps> = ({
  fields,
  renderField,
  highlightFields = [],
}) => {
  const highlightSet = new Set(highlightFields);
  return (
    <dl className="divide-y divide-gray-100">
      {fields.map((field, index) => {
        const isHighlighted = highlightSet.has(field.name);
        return (
          <div
            key={field.name}
            className={`py-2 sm:grid sm:grid-cols-3 sm:gap-3 transition-colors ${
              isHighlighted
                ? 'bg-amber-50 -mx-2 px-2 rounded-md ring-1 ring-amber-200'
                : ''
            }`}
          >
            <dt
              className={`text-xs font-medium ${isHighlighted ? 'text-amber-900' : 'text-gray-900'}`}
            >
              {field.label}
              {isHighlighted && (
                <span className="ml-1.5 text-[10px] font-normal text-amber-600">
                  (required)
                </span>
              )}
            </dt>
            <dd className="mt-0.5 text-xs sm:col-span-2 sm:mt-0">
              {renderField(field, index)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

export const ClientForm: React.FC<ClientFormProps> = ({
  isEditing,
  clientData,
  onFieldChange,
  clientId,
  matterId,
  highlightFields = [],
}) => {
  // Helper to get nested values safely
  const getValue = (obj: any, path: string): any => {
    const keys = path.split('.');
    return keys.reduce(
      (o, key) => (o && o[key] !== undefined ? o[key] : ''),
      obj,
    );
  };

  // Format SSN for display
  const formatSSN = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 5)
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
  };

  // Handle SSN change with formatting
  const handleSSNChange = (value: string) => {
    onFieldChange('ssn', formatSSN(value));
  };

  // Phone number specific handlers
  const handleAddPhone = () => {
    const phones = [
      ...(clientData.phone_numbers || []),
      { id: '', phone_number: '' },
    ];
    onFieldChange('phone_numbers', JSON.stringify(phones));
  };

  const handleRemovePhone = (index: number) => {
    const phones = [...(clientData.phone_numbers || [])];
    phones.splice(index, 1);
    onFieldChange('phone_numbers', JSON.stringify(phones));
  };

  const handlePhoneChange = (index: number, value: string) => {
    const phones = [...(clientData.phone_numbers || [])];
    phones[index].phone_number = value;
    onFieldChange('phone_numbers', JSON.stringify(phones));
  };

  // Get non-empty fields for display mode
  const nonEmptyFields = isEditing
    ? CLIENT_FIELDS
    : CLIENT_FIELDS.filter((field) => {
        const value = getValue(clientData, field.name);
        return value !== null && value !== undefined && value !== '';
      });

  const hiddenFieldsCount = CLIENT_FIELDS.length - nonEmptyFields.length;

  // Render main form fields based on edit mode and empty fields
  const renderFormFields = () => {
    if (isEditing) {
      return (
        <ClientFieldsLayout
          fields={CLIENT_FIELDS}
          highlightFields={highlightFields}
          renderField={(field) => (
            <ClientFormField
              name={field.name}
              value={getValue(clientData, field.name)}
              onChange={
                field.name === 'ssn'
                  ? handleSSNChange
                  : (value) => onFieldChange(field.name, value)
              }
              type={field.type}
              placeholder={field.placeholder}
              multiline={field.multiline}
              pattern={field.pattern}
              maxLength={field.maxLength}
              isSelect={field.isSelect}
              options={field.options}
            />
          )}
        />
      );
    }
    if (nonEmptyFields.length > 0) {
      return (
        <ClientFieldsLayout
          fields={nonEmptyFields}
          renderField={(field) => {
            const value = getValue(clientData, field.name);
            const age =
              field.name === 'date_of_birth' && typeof value === 'string'
                ? calculateAge(value)
                : null;
            return (
              <div
                className={
                  field.multiline ? 'whitespace-pre-wrap text-xs' : 'text-xs'
                }
              >
                <InteractiveDisplayField
                  label={field.label}
                  value={value}
                  multiline={field.multiline}
                />
                {age !== null && (
                  <span className="ml-1 text-xs font-medium text-gray-500">
                    (Age: {age})
                  </span>
                )}
              </div>
            );
          }}
        />
      );
    }
    return (
      <p className="text-xs text-gray-500 italic pb-1">Nothing here yet.</p>
    );
  };

  return (
    <div className="space-y-0">
      {renderFormFields()}

      <PhoneNumberFields
        phoneNumbers={clientData.phone_numbers || []}
        isEditing={isEditing}
        onAdd={handleAddPhone}
        onRemove={handleRemovePhone}
        onChange={handlePhoneChange}
      />

      {clientId && (
        <HipaaSignatureField
          clientId={clientId}
          isEditing={isEditing}
          matterId={matterId}
          clientName={
            `${clientData.first_name || ''} ${clientData.last_name || ''}`.trim() ||
            clientData.name
          }
          clientEmail={clientData.email}
          clientPhone={clientData.phone_numbers?.[0]?.phone_number}
        />
      )}

      {clientId && matterId && (
        <ClientPhotoIdField
          clientId={clientId}
          matterId={matterId}
          isEditing={isEditing}
        />
      )}

      <InjuriesFields
        personId={clientData.person_id}
        matterId={matterId}
        isEditing={isEditing}
      />

      {!isEditing && hiddenFieldsCount > 0 && (
        <div className="border-t border-gray-100 pt-1">
          <p className="text-[10px] text-gray-400 italic">
            {hiddenFieldsCount} empty{' '}
            {hiddenFieldsCount === 1 ? 'field' : 'fields'} not shown.
          </p>
        </div>
      )}
    </div>
  );
};
